<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useIntervalFn, useTimeoutFn } from '@vueuse/core';
  import { useSound } from '@vueuse/sound';
  import { usePrinting } from '@/composition/use-printing';
  import { usePrintHealth } from '@/composition/printing/use-print-health';
  import { usePrintHealthStore } from '@/stores/print-health.store';
  import { Globals } from '@/modules/Globals';
  import {
    PRINT_FAIL_ALARM_INTERVAL_MS,
    SUCCESS_CHIP_HIDE_MS,
    SUCCESS_CHIP_MAX_DISPLAY,
  } from '@/lib/print/print-health.utils';
  import AppPrintingWidgetPanel from './AppPrintingWidgetPanel.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const printing = usePrinting();
  const healthStore = usePrintHealthStore();
  const { offlineAssignedPrinters } = usePrintHealth();

  const isOpen = ref(false);

  // #region old-button parity (status dot / upgrade badge)

  const printerStatus = computed(() => printing.status.value);
  const outdatedVersion = computed(() => printing.isOutdatedVersion.value);
  const upgradeAvailable = computed(() => printing.upgradeAvailable.value);

  // #endregion

  // #region failures: badge + alarm (ack on panel open)

  const failureCount = computed(() => healthStore.failureCount);
  const hasUnackedFailures = computed(() => healthStore.hasUnackedFailures);
  const hasOffline = computed(() => offlineAssignedPrinters.value.length > 0);

  // Opening the panel acknowledges the current entries on THIS device only:
  // the alarm stops, the badge persists until resolution, and a NEW dedupe
  // key re-arms the alarm (same model as the incoming-orders alert).
  watch(isOpen, (open) => {
    if (open) healthStore.acknowledgeFailures();
  });

  // Autoplay before the first user gesture is best-effort (same accepted
  // limitation as order sounds); the badge is the guaranteed signal.
  const failSound = useSound(`${Globals.uploadsPath}/sounds/print-fail.mp3`);
  const alarmInterval = useIntervalFn(
    () => failSound.play(),
    PRINT_FAIL_ALARM_INTERVAL_MS,
    { immediate: false, immediateCallback: true }
  );

  watch(
    () => healthStore.hasUnackedFailures,
    (unacked) => {
      if (unacked) {
        // A failure landing while the panel is open is already being seen —
        // ack it immediately instead of ringing at the user.
        if (isOpen.value) {
          healthStore.acknowledgeFailures();
          return;
        }
        if (!alarmInterval.isActive.value) alarmInterval.resume();
      } else {
        alarmInterval.pause();
        failSound.stop();
      }
    },
    { immediate: true }
  );

  // #endregion

  // #region success counter chip

  const chipVisible = ref(false);
  const chipFading = ref(false);

  const chipDisplay = computed(() =>
    healthStore.successChipCount > SUCCESS_CHIP_MAX_DISPLAY
      ? `${SUCCESS_CHIP_MAX_DISPLAY}+`
      : `+${healthStore.successChipCount}`
  );

  // Fade out (opacity only), then remove from DOM and reset the burst count
  // so the next success reads fresh. Both timers restart on every print.
  const removeTimer = useTimeoutFn(
    () => {
      chipVisible.value = false;
      chipFading.value = false;
      healthStore.resetSuccessChip();
    },
    400,
    { immediate: false }
  );
  const fadeTimer = useTimeoutFn(
    () => {
      chipFading.value = true;
      removeTimer.start();
    },
    SUCCESS_CHIP_HIDE_MS,
    { immediate: false }
  );

  watch(
    () => healthStore.successChipCount,
    (count) => {
      if (count <= 0) {
        // Count cleared externally (branch switch): drop the chip at once
        // instead of leaving a stale "+0" until the fade timer fires.
        fadeTimer.stop();
        removeTimer.stop();
        chipVisible.value = false;
        chipFading.value = false;
        return;
      }
      removeTimer.stop();
      chipFading.value = false;
      chipVisible.value = true;
      fadeTimer.start();
    }
  );

  // #endregion
</script>

<template>
  <div class="printing-widget">
    <!-- Success counter chip: inline LEFT of the button (position + shape
         distinguish it from the corner failure badge, not just color).
         Keyed on the count: each increment recreates the single small box,
         restarting the pop + glow animations with zero idle cost. -->
    <div
      v-if="chipVisible"
      class="success-chip"
      :class="{ fading: chipFading }"
    >
      <div :key="healthStore.successChipCount" class="chip-box">
        <PosUiIcon name="receipt" class="chip-icon" />
        <span class="chip-count">{{ chipDisplay }}</span>
      </div>
    </div>

    <v-menu
      v-model="isOpen"
      bottom
      left
      offset-y
      :close-on-content-click="false"
    >
      <template v-slot:activator="{ on, attrs }">
        <!-- .pinging rings around the WHOLE button (same language as the
             incoming-orders pill: red + ping = needs action) while UNACKED —
             opening the panel stops it, like the alarm sound. transform/
             opacity only: compositor-friendly on old tablets. -->
        <button
          type="button"
          class="widget-button"
          :class="{ pinging: hasUnackedFailures }"
          :aria-label="$t('printWidget.panelTitle')"
          v-bind="attrs"
          v-on="on"
        >
          <span class="button-content">
            <PosUiIcon name="printer" class="printer-icon" />
            <!-- Upgrade badge is additive: the status dot must stay visible so
                 a mid-session legacy disconnect is still signaled in red. -->
            <template v-if="upgradeAvailable">
              <span class="upgrade-label">{{
                $t('printingV2.upgradeBadge')
              }}</span>
              <PosUiIcon
                v-tooltip="$t('printingV2.upgradeTooltip')"
                name="arrow-up-circle"
                class="upgrade-icon"
              />
            </template>
            <span
              v-if="printerStatus === 0 || outdatedVersion"
              v-tooltip="$t('printWidget.statusDisconnected')"
              class="status-dot is-disconnected"
            />
            <v-progress-circular
              v-else-if="printerStatus === 1"
              v-tooltip="$t('printWidget.statusConnecting')"
              class="status-spinner"
              :size="14"
              :width="2"
              color="rgb(var(--c-amber-500))"
              indeterminate
            ></v-progress-circular>
            <span
              v-else
              v-tooltip="$t('printWidget.statusConnected')"
              class="status-dot is-connected"
            />
          </span>

          <span
            v-if="failureCount"
            class="failure-badge"
            :title="$t('printWidget.failuresTitle')"
            >{{ failureCount }}</span
          >
          <span
            v-else-if="hasOffline"
            class="offline-indicator"
            :title="$t('printWidget.offlineTitle')"
          >
            <PosUiIcon name="alert" class="offline-icon" />
          </span>
        </button>
      </template>
      <AppPrintingWidgetPanel @close="isOpen = false" />
    </v-menu>
  </div>
</template>

<style scoped lang="scss">
  .printing-widget {
    @apply tw-flex tw-items-center;

    .success-chip {
      @apply tw-mr-2 tw-transition-opacity tw-duration-300;

      &.fading {
        @apply tw-opacity-0;
      }

      .chip-box {
        @apply tw-relative tw-flex tw-items-center tw-rounded-full tw-bg-emerald-600 tw-px-2.5 tw-py-1 tw-text-sm tw-font-semibold tw-text-on-color;
        animation: chip-pop 0.22s ease-out;

        // Pre-composited glow ring: opacity-only animation (no box-shadow /
        // filter per frame).
        &::after {
          content: '';
          @apply tw-pointer-events-none tw-absolute tw-rounded-full;
          top: -4px;
          right: -4px;
          bottom: -4px;
          left: -4px;
          border: 2px solid rgb(var(--c-emerald-400) / 0.9);
          opacity: 0;
          animation: chip-glow 0.45s ease-out;
        }

        .chip-icon {
          @apply tw-mr-1 tw-text-base;
        }

        .chip-count {
          @apply tw-leading-none;
        }
      }
    }

    .widget-button {
      @apply tw-relative tw-flex tw-h-11 tw-cursor-pointer tw-items-center tw-rounded-full tw-border-0 tw-bg-transparent tw-px-2.5 tw-transition-colors tw-duration-150;

      &:hover {
        @apply tw-bg-neutral-100;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      // Ping ring behind the button content. Content is lifted above it so
      // the ring's solid start-of-cycle frame never covers the icon or the
      // badge.
      &.pinging::after {
        content: '';
        @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-animate-ping tw-rounded-full tw-bg-red-400;
        z-index: 0;
      }

      .button-content {
        @apply tw-relative tw-z-[1] tw-flex tw-items-center;
      }

      .printer-icon {
        @apply tw-text-2xl tw-text-neutral-600;
      }

      .upgrade-label {
        @apply tw-ml-1 tw-text-sm tw-font-medium tw-text-neutral-700;
      }

      .upgrade-icon {
        @apply tw-ml-1 tw-text-xl tw-text-amber-500;
      }

      .status-dot {
        @apply tw-ml-1.5 tw-h-2 tw-w-2 tw-rounded-full;

        &.is-connected {
          @apply tw-bg-emerald-500;
        }

        &.is-disconnected {
          @apply tw-bg-red-500;
        }
      }

      .status-spinner {
        @apply tw-ml-1.5;
      }

      .failure-badge {
        @apply tw-absolute tw-z-[2] tw-flex tw-h-[18px] tw-min-w-[18px] tw-items-center tw-justify-center tw-rounded-full tw-bg-red-600 tw-px-1 tw-text-[11px] tw-font-bold tw-leading-none tw-text-on-color;
        top: 0;
        right: -2px;
        box-shadow: 0 0 0 2px rgb(var(--c-surface));
      }

      .offline-indicator {
        @apply tw-absolute tw-z-[2] tw-flex tw-h-[18px] tw-w-[18px] tw-items-center tw-justify-center tw-rounded-full tw-bg-amber-500 tw-text-on-color;
        top: 0;
        right: -2px;
        box-shadow: 0 0 0 2px rgb(var(--c-surface));

        .offline-icon {
          @apply tw-text-[11px];
        }
      }
    }
  }

  @keyframes chip-pop {
    0% {
      transform: scale(1);
    }
    40% {
      transform: scale(1.18);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes chip-glow {
    0% {
      opacity: 1;
      transform: scale(0.9);
    }
    100% {
      opacity: 0;
      transform: scale(1.25);
    }
  }
</style>
